import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, Play, ShoppingBag, CheckCircle2 } from "lucide-react";
import PageMeta from "../components/PageMeta";
import { NexusHardwareShowcase } from "../components/NexusVisuals";
import { NEXUS_HARDWARE, NEXUS_POWER, NEXUS_COMMS } from "../data/nexusData";
import { NEXUS_DEMO_URL } from "../lib/urls";

export default function NexusHardwarePage({ onTrialClick }) {
  return (
    <div className="min-h-screen" data-testid="nexus-hardware-page">
      <PageMeta
        title="Geminii Nexus · Hardware"
        description="Industrial nodes, gateways, and a curated sensor library — designed in-house, pre-loaded with the Nexus stack, plug-and-play with your tenant."
        path="/nexus/hardware"
      />

      {/* HERO */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-2 text-sm text-geminii-dim mb-6">
            <Link to="/nexus" className="hover:text-white transition-colors flex items-center gap-1">
              <ChevronLeft size={14} /> Nexus
            </Link>
            <span>/</span>
            <span className="text-nexus-blue">Hardware</span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-4"
          >
            The metal behind the cloud.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-geminii-muted max-w-2xl leading-relaxed mb-10"
          >
            Every device that talks to Geminii Nexus is engineered, manufactured, and supported by the same team that builds the platform. No third-party SCADA gear, no firmware lottery, no integration tax. Just industrial hardware purpose-built for this exact stack.
          </motion.p>

          <div className="flex flex-wrap gap-3">
            <a
              href={NEXUS_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hardware-hero-demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-nexus-blue text-geminii-deep font-semibold text-sm hover:shadow-[0_0_20px_rgba(30,159,188,0.4)] transition-all"
            >
              <Play size={13} className="fill-geminii-deep" />
              See the hardware in the live demo
            </a>
            <Link
              to="/nexus"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-nexus-blue/50 hover:text-nexus-blue transition-all"
            >
              Back to Nexus overview
            </Link>
          </div>
        </div>
      </section>

      {/* Product cards */}
      <section className="py-8 md:py-12 border-t border-white/[0.04]" data-testid="nexus-hardware-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {NEXUS_HARDWARE.map((h, i) => (
              <motion.div
                key={h.name}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-geminii-card border border-nexus-blue/15 rounded-2xl p-6 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-2">
                  <h.icon size={20} className="text-nexus-blue" />
                  <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">{h.role}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-white mb-2">{h.name}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed mb-5">{h.headline}</p>
                <ul className="space-y-2 mb-5 flex-1">
                  {h.specs.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-xs text-white/90 leading-relaxed">
                      <CheckCircle2 size={12} className="text-nexus-blue mt-1 flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <span className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">Spec sheet on request</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase visual */}
      <section className="py-8 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <NexusHardwareShowcase />
        </div>
      </section>

      {/* Power x Comms summary */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Power profiles</span>
              <h2 className="font-display font-bold text-xl md:text-2xl text-white mt-2 mb-4">Three ways to keep a node alive.</h2>
              <div className="space-y-2.5">
                {NEXUS_POWER.map((p) => (
                  <div key={p.name} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3.5 flex items-start gap-3">
                    <p.icon size={16} className="text-nexus-blue mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display font-bold text-sm text-white">{p.name}</span>
                        <span className="text-[10px] font-mono text-geminii-dim">{p.spec}</span>
                      </div>
                      <p className="text-xs text-geminii-muted mt-0.5">{p.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Connectivity options</span>
              <h2 className="font-display font-bold text-xl md:text-2xl text-white mt-2 mb-4">Pair a radio to every site.</h2>
              <div className="space-y-2.5">
                {NEXUS_COMMS.map((c) => (
                  <div key={c.name} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3.5 flex items-start gap-3">
                    <c.icon size={16} className="text-nexus-blue mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display font-bold text-sm text-white">{c.name}</span>
                        <span className="text-[10px] font-mono text-geminii-dim">{c.range}</span>
                      </div>
                      <p className="text-xs text-geminii-muted mt-0.5">{c.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop teaser */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-geminii-card border border-nexus-blue/30 rounded-2xl p-8 md:p-12 relative overflow-hidden" data-testid="nexus-shop-teaser">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-nexus-blue/10 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-nexus-blue/10 border border-nexus-blue/25 mb-4">
                  <ShoppingBag size={11} className="text-nexus-blue" />
                  <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Online shop · launching soon</span>
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Buy nodes, gateways, and sensors — pre-bound to your tenant.</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">
                  Add to cart, check out, ship to site. The catalogue arrives at the door pre-provisioned for the organisation that ordered it. Power it on; it's already in the fleet.
                </p>
              </div>
              <div className="md:text-right">
                <Link
                  to="/contact?intent=nexus-shop"
                  data-testid="nexus-shop-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-nexus-blue text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(30,159,188,0.4)] transition-all"
                >
                  Join the launch list <ArrowRight size={14} />
                </Link>
                <p className="text-[10px] font-mono text-geminii-dim mt-3 uppercase tracking-wider">Launch-list customers get priority units</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">
            One stack. From the sensor to the operator.
          </h2>
          <p className="text-geminii-muted mb-8 max-w-xl mx-auto leading-relaxed">
            No vendor sprawl. No firmware mismatch. No "we don't support that radio." A single team owns every layer from the field to the cloud.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={NEXUS_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hardware-closing-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-nexus-blue text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(30,159,188,0.4)] transition-all"
            >
              <Play size={14} className="fill-geminii-deep" />
              Open the live demo
            </a>
            <button
              onClick={onTrialClick}
              data-testid="hardware-closing-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-nexus-blue/50 hover:text-nexus-blue transition-all"
            >
              Request early access <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
